<template>
    <themed-modal
        name="tour-invite-modal"
        title="Invite Attendees"
        size="lg"
        :show-footer="true"
        @closed="closeModal"
    >
        <div class="tour-invite-modal">
            <!-- Current Attendees Section -->
            <div class="tour-invite-modal__section">
                <h4 class="tour-invite-modal__section-title">Current Attendees ({{ attendees.length }})</h4>
                
                <div v-if="attendees.length === 0" class="tour-invite-modal__empty">
                    No attendees yet. Invite users below.
                </div>

                <div v-else class="tour-invite-modal__attendees">
                    <div
                        v-for="attendee in attendees"
                        :key="attendee.id"
                        class="tour-invite-modal__attendee"
                    >
                        <div class="tour-invite-modal__attendee-info">
                            <div class="tour-invite-modal__attendee-name">{{ attendee.display_name }}</div>
                            <div class="tour-invite-modal__attendee-email">{{ attendee.display_email }}</div>
                        </div>
                        <div class="tour-invite-modal__attendee-actions">
                            <themed-badge :variant="getStatusVariant(attendee.invitation_status)">
                                {{ attendee.invitation_status }}
                            </themed-badge>
                            <themed-button
                                @click="removeAttendee(attendee)"
                                size="sm"
                                variant="destructive"
                            >
                                Remove
                            </themed-button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Invite Form Section -->
            <div class="tour-invite-modal__section">
                <h4 class="tour-invite-modal__section-title">Invite New Attendees</h4>

                <!-- Select Previous Attendees -->
                <themed-form-field 
                    label="Select Previous Attendees"
                    help-text="Choose attendees you've invited before"
                >
                    <themed-multi-select
                        v-model="selectedPreviousAttendees"
                        :options="attendeeOptions"
                        placeholder="Select from previous tours"
                        :searchable="true"
                        :close-on-select="false"
                    />
                </themed-form-field>

                <!-- Add New Email -->
                <themed-form-field label="Add New Email">
                    <div class="tour-invite-modal__email-input-wrapper">
                        <div class="tour-invite-modal__email-input">
                            <themed-input
                                v-model="newEmail"
                                type="email"
                                placeholder="email@example.com"
                                @keyup.enter="addEmail"
                            />
                            <themed-button
                                @click="addEmail"
                                variant="secondary"
                                :disabled="!newEmail"
                            >
                                Add
                            </themed-button>
                        </div>
                        
                        <div v-if="emailList.length > 0" class="tour-invite-modal__email-tags">
                            <div
                                v-for="(email, index) in emailList"
                                :key="index"
                                class="tour-invite-modal__email-tag"
                            >
                                <span>{{ email }}</span>
                                <button @click="removeEmail(index)" class="tour-invite-modal__email-tag-remove">
                                    &times;
                                </button>
                            </div>
                        </div>
                    </div>
                </themed-form-field>
            </div>
        </div>

        <template #footer>
            <themed-button
                @click="closeModal"
                variant="secondary"
            >
                Cancel
            </themed-button>
            <themed-button
                @click="sendInvitations"
                variant="default"
                :disabled="!canSendInvitations || sending"
            >
                {{ sending ? 'Sending...' : 'Send Invitations' }}
            </themed-button>
        </template>
    </themed-modal>
</template>

<script>
import axios from 'axios';
import ThemedModal from '../ThemedModal.vue';
import ThemedButton from '../ThemedButton.vue';
import ThemedBadge from '../ThemedBadge.vue';
import ThemedInput from '../ThemedInput.vue';
import ThemedFormField from '../form/ThemedFormField.vue';
import ThemedMultiSelect from '../form/ThemedMultiSelect.vue';

export default {
    name: 'TourInviteModal',

    components: {
        ThemedModal,
        ThemedButton,
        ThemedBadge,
        ThemedInput,
        ThemedFormField,
        ThemedMultiSelect,
    },

    props: {
        tour: {
            type: Object,
            default: null,
        },
    },

    data() {
        return {
            attendees: [],
            previousAttendees: [],
            selectedPreviousAttendees: [],
            newEmail: '',
            emailList: [],
            sending: false,
            loading: false,
        };
    },

    computed: {
        canSendInvitations() {
            return this.selectedPreviousAttendees.length > 0 || this.emailList.length > 0;
        },

        attendeeOptions() {
            return this.previousAttendees.map(attendee => ({
                value: attendee.email,
                label: `${attendee.name} (${attendee.email})`,
            }));
        },
    },

    mounted() {
        this.$modal.show('tour-invite-modal');
        if (this.tour) {
            this.loadAttendees();
            this.loadPreviousAttendees();
        }
    },

    methods: {
        closeModal() {
            this.$modal.hide('tour-invite-modal');
        },
        
        async loadAttendees() {
            if (!this.tour) return;
            
            this.loading = true;
            try {
                const response = await axios.get(`/tours/${this.tour.id}/attendees`);
                this.attendees = response.data;
            } catch (error) {
                console.error('Error loading attendees:', error);
                if (this.$toast) {
                    this.$toast.error('Failed to load attendees');
                }
            } finally {
                this.loading = false;
            }
        },

        async loadPreviousAttendees() {
            try {
                const response = await axios.get(`/tours/${this.tour.id}/attendees/available`);
                this.previousAttendees = response.data;
            } catch (error) {
                console.error('Error loading previous attendees:', error);
                if (this.$toast) {
                    this.$toast.error('Failed to load previous attendees');
                }
            }
        },

        addEmail() {
            if (this.newEmail && this.isValidEmail(this.newEmail)) {
                if (!this.emailList.includes(this.newEmail)) {
                    this.emailList.push(this.newEmail);
                    this.newEmail = '';
                } else {
                    if (this.$toast) {
                        this.$toast.error('Email already added');
                    }
                }
            } else {
                if (this.$toast) {
                    this.$toast.error('Please enter a valid email');
                }
            }
        },

        removeEmail(index) {
            this.emailList.splice(index, 1);
        },

        isValidEmail(email) {
            const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            return re.test(email);
        },

        async sendInvitations() {
            if (!this.canSendInvitations) {
                if (this.$toast) {
                    this.$toast.error('Please select attendees or provide email addresses to invite');
                }
                return;
            }

            // Combine selected previous attendees with new emails
            const allEmails = [
                ...this.selectedPreviousAttendees,
                ...this.emailList
            ];

            this.sending = true;
            try {
                await axios.post(`/tours/${this.tour.id}/invite`, {
                    user_ids: [],
                    emails: allEmails,
                });
                if (this.$toast) {
                    this.$toast.success('Invitations sent successfully');
                }
                this.selectedPreviousAttendees = [];
                this.emailList = [];
                await this.loadAttendees();
                await this.loadPreviousAttendees();
                this.$emit('invited');
            } catch (error) {
                console.error('Error sending invitations:', error);
                const message = error.response?.data?.message || 'Failed to send invitations';
                if (this.$toast) {
                    this.$toast.error(message);
                }
            } finally {
                this.sending = false;
            }
        },

        async removeAttendee(attendee) {
            const result = await this.$swal({
                title: 'Remove Attendee?',
                text: `Remove ${attendee.display_name} from this tour?`,
                type: 'warning',
                buttonsStyling: false,
                confirmButtonText: 'Yes - Remove',
                confirmButtonClass: 'btn btn-red',
                showCancelButton: true,
                cancelButtonClass: 'btn btn-secondary mr-4',
            });

            if (!result.value) {
                return;
            }

            try {
                await axios.delete(`/tours/${this.tour.id}/attendees/${attendee.id}`);
                if (this.$toast) {
                    this.$toast.success('Attendee removed successfully');
                }
                this.loadAttendees();
                this.$emit('invited');
            } catch (error) {
                console.error('Error removing attendee:', error);
                if (this.$toast) {
                    this.$toast.error('Failed to remove attendee');
                }
            }
        },

        getStatusVariant(status) {
            const variants = {
                pending: 'secondary',
                accepted: 'default',
                declined: 'destructive',
            };
            return variants[status] || 'outline';
        },
    },
};
</script>
